import React, { useState, useCallback, useEffect } from "react";
import { useSelector } from 'react-redux';
import { useDropzone } from "react-dropzone";
import AddCustomer from "../../views/Orders/AddCustomer.js";
import AddContactInfo from "../../inputs/select/SelectContactInfo.js";
import { baseOrderObject } from "../../views/Orders/BaseOrderObject.js";
import HelpBox from "../../components/HelpBox";

import JSON5 from 'json5';

import { getCookie, csvToArray, arrayToCSV } from '../../util.js';

import {
  Pane,
  Paragraph,
  Button,
  Table,
  Text,
  toaster,
  Card,
  Tooltip,
  Badge,
  PaperclipIcon,
  InfoSignIcon,
  Spinner,
  ErrorIcon,
} from 'evergreen-ui';

// Helper function to validate a row
const validateRow = (row, headers) => {
  const errors = {};
  const warnings = {};

  // Guard against undefined/null row
  if (!row || !Array.isArray(row)) {
    return { errors: { format: 'Invalid row format' }, warnings: {} };
  }

  // Get indices for key fields
  const retailPriceIdx = headers.indexOf('retail_price');
  const netPriceIdx = headers.indexOf('net_price');
  const quantityIdx = headers.indexOf('quantity');
  const isbnIdx = headers.indexOf('isbn');

  // Validate prices
  if (retailPriceIdx >= 0 && netPriceIdx >= 0) {
    const retail = parseFloat(row[retailPriceIdx] || '0');
    const net = parseFloat(row[netPriceIdx] || '0');
    if (net > retail) {
      errors['net_price'] = 'Net price cannot exceed retail price';
    }
    if (retail < 0) {
      errors['retail_price'] = 'Price cannot be negative';
    }
  }

  // Validate quantity
  if (quantityIdx >= 0) {
    const qty = parseInt(row[quantityIdx] || '0');
    if (isNaN(qty) || qty <= 0) {
      errors['quantity'] = 'Quantity must be a positive number';
    }
  }

  // Validate ISBN
  if (isbnIdx >= 0) {
    if (!row[isbnIdx] || !row[isbnIdx].toString().trim()) {
      errors['isbn'] = 'ISBN is required';
    }
  }

  // Validate dates
  const dateFields = ['ship_date', 'paid_date'];
  dateFields.forEach(field => {
    const idx = headers.indexOf(field);
    if (idx >= 0 && row[idx] && row[idx].toString().trim()) {
      const date = row[idx].toString().trim();
      if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
        errors[field] = 'Invalid date format (use YYYY-MM-DD)';
      }
    }
  });

  headers.forEach((header, index) => {
    const value = row[index];

    if (row.type === 'expense') {
      // Check if we have either a valid product ID or ISBN
      const hasProductId = row[headers.indexOf('product')] && row[headers.indexOf('product')].trim();
      const hasIsbn = row[headers.indexOf('isbn')] && row[headers.indexOf('isbn')].trim();
      const hasCategory = row[headers.indexOf('category')] && row[headers.indexOf('category')].trim();

      // Only validate product/ISBN if no category is provided
      if (!hasCategory) {
        if (!hasProductId && !hasIsbn) {
          errors['product'] = 'Expense must be associated with either a product (by ID or ISBN) or a category';
          errors['isbn'] = 'Expense must be associated with either a product (by ID or ISBN) or a category';
        } else {
          // If product ID is provided, validate it's a number
          if (hasProductId && !/^\d+$/.test(row[headers.indexOf('product')])) {
            errors['product'] = 'ID must be an integer';
          }
          // If ISBN is provided, validate it's a number
          if (hasIsbn && !/^\d+$/.test(row[headers.indexOf('isbn')])) {
            errors['isbn'] = 'ISBN must only contain numbers';
          }
        }
      }

      // Validate required fields
      if (header === 'amount') {
        if (!value || !value.trim()) {
          errors[header] = 'Cannot import expense without amount';
        } else if (!/^\d*\.?\d*$/.test(value)) {
          errors[header] = 'Formatted incorrectly - only digits and one period allowed';
        }
      }

      if (header === 'expense_date') {
        if (!value || !value.trim()) {
          errors[header] = 'Cannot import expense without expense_date';
        } else {
          const datePattern = /^\d{4}-\d{2}-\d{2}$/;
          if (!datePattern.test(value)) {
            errors[header] = 'Formatted incorrectly - dates must be formatted as YYYY-MM-DD';
          } else {
            const date = new Date(value);
            if (isNaN(date.getTime())) {
              errors[header] = 'Not a valid date';
            }
          }
        }
      }
    }
  });

  return { errors, warnings };
};

function CsvUpload(props) {
  const currentpublisher = useSelector((state) => state.currentpublisher.value);
  const [dropped, setDropped] = useState(false);
  const [finished, setFinished] = useState(false);
  const [importError, setImportError] = useState(false);
  const [valid, setValid] = useState(false);
  const [preview, setPreview] = useState(false);
  const [results, setResults] = useState([]);
  const [staged, setStaged] = useState(baseOrderObject);
  const [isLoading, setIsLoading] = useState(false);

  const handleChooseInfo = (itemString, itemType) => {
    let item = JSON.parse(itemString)
    handleUpdateStaged({ key: itemType, val: item });
  }

  const handleUpdateStaged = obj => {
    if (obj.key === "customer") {
      setStaged({ ...baseOrderObject, [obj.key]: obj.val })

    } else {
      setStaged(oldState => ({ ...oldState, [obj.key]: obj.val }))
    }

  }

  const hasCustomer = staged.customer && staged.customer.id
    ? true
    : false;

  const hasEmail = staged.orderEmail && staged.orderEmail.id
    ? true
    : false;

  const hasPhone = staged.orderPhone && staged.orderPhone.id
    ? true
    : false;

  const hasAddress = staged.orderAddress && staged.orderAddress.id
    ? true
    : false;

  const importItems = async () => {
    setIsLoading(true);
    const csrftoken = getCookie('csrftoken');

    // Helper function to show toasts with proper cleanup
    const showToast = (type, message, options = {}) => {
      // Clear any existing toasts
      toaster.closeAll();

      // Create and focus a temporary button to trigger React's focus handling
      const tempButton = document.createElement('button');
      tempButton.style.position = 'fixed';
      tempButton.style.opacity = '0';
      document.body.appendChild(tempButton);
      tempButton.focus();

      const defaultOptions = {
        duration: 30000,
        id: `csv-import-${type}`
      };

      // Show the toast
      switch(type) {
        case 'error':
          toaster.danger(message, { ...defaultOptions, ...options });
          break;
        case 'warning':
          toaster.warning(message, { ...defaultOptions, ...options });
          break;
        case 'success':
          toaster.success(message, { ...defaultOptions, duration: 15000, ...options });
          break;
        case 'info':
          toaster.notify(message, { ...defaultOptions, duration: null, ...options });
          break;
      }

      // Clean up the temporary button
      setTimeout(() => {
        document.body.removeChild(tempButton);
      }, 100);
    };

    // Validate required data
    if (!results) {
      const msg = "No CSV data to import";
      console.error("CSV Import Error:", msg);
      showToast('error', msg);
      return;
    }

    if (props.itemType === "order" && !staged.customer?.id) {
      const msg = "Please select a customer before importing orders";
      console.error("CSV Import Error:", msg);
      showToast('error', msg);
      return;
    }

    const importMsg = "Importing records...";
    console.log("CSV Import Status:", importMsg);
    showToast('info', importMsg);

    let inputData = {
      type: props.itemType,
      contents: results,
      pubId: currentpublisher.id
    };

    if (props.itemType === "order") {
      let customerInfo = {
        customer: staged.customer.id,
        phone: staged.orderPhone?.id || "",
        address: staged.orderAddress?.id || "",
        email: staged.orderEmail?.id || ""
      }
      inputData = { ...inputData, customer: customerInfo }
    }

    try {
      const response = await fetch('/import_csv/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': csrftoken
        },
        body: JSON.stringify(inputData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        const errorMsg = errorData.message || "Error importing records";
        console.error("CSV Import Error:", errorMsg);
        showToast('error', errorMsg);
        return;
      }

      const data = await response.json();
      if (data.message === "Items imported") {
        const successMsg = "Finished importing records!";
        console.log("CSV Import Success:", successMsg);
        showToast('success', successMsg);
        setDropped(false);
        setFinished(false);
        setValid(false);
        setResults(false);
        setPreview(false);
        setIsLoading(false);
        props.handleToggleCsvMode();
      } else {
        console.warn("CSV Import Warning:", data.message);
        showToast('warning', data.message);
      }
    } catch (error) {
      const errorMsg = `Error importing records: ${error.message}`;
      console.error("CSV Import Error:", errorMsg);
      console.error("Full error details:", error);
      showToast('error', errorMsg);
      setIsLoading(false);
    }
  };

  const fileDrop = async (file, otherInputs) => {
    setIsLoading(true);
    const csrftoken = getCookie('csrftoken');

    var reader = new FileReader();
    reader.onload = async function (e) {
      var contents = e.target.result;
      setResults(contents);
      setPreview(true);

      let inputData = {
        type: props.itemType,
        contents: contents,
        pubId: currentpublisher.id,
      }

      if (props.itemType === "order") {
        let customerInfo = {
          customer: otherInputs.customer.id,
          phone: otherInputs.orderPhone ? otherInputs.orderPhone.id : "",
          address: otherInputs.orderAddress ? otherInputs.orderAddress.id : "",
          email: otherInputs.orderEmail ? otherInputs.orderEmail.id : ""
        }
        inputData = { ...inputData, customer: customerInfo }
      }

      try {
        const response = await fetch('/validate_csv/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': csrftoken
          },
          body: JSON.stringify(inputData)
        });

        const data = await response.json();
        setFinished(true);
        let obj = JSON5.parse(data.message);

        // Handle different item types
        if (props.itemType === 'creator') {
          const hasUnknownPaymentTypes = obj.some(row =>
            row.errors && row.errors.unknown_payment_types
          );

          if (hasUnknownPaymentTypes) {
            const unknownTypes = obj
              .filter(row => row.errors && row.errors.unknown_payment_types)
              .flatMap(row => row.errors.unknown_payment_types);

            if (props.onValidationError) {
              props.onValidationError({ unknown_payment_types: [...new Set(unknownTypes)] });
            }

            // Remove the unknown_payment_types from the validation errors
            obj = obj.map(row => {
              if (row.errors && row.errors.unknown_payment_types) {
                const { unknown_payment_types, ...otherErrors } = row.errors;
                return { ...row, errors: otherErrors };
              }
              return row;
            });
          }
        } else if (props.itemType === 'customer') {
          const hasUnknownPaymentTerms = obj.some(row =>
            row.errors && row.errors.unknown_payment_terms
          );
          const hasUnknownCustomerClasses = obj.some(row =>
            row.errors && row.errors.unknown_customer_classes
          );

          if (hasUnknownPaymentTerms || hasUnknownCustomerClasses) {
            const unknownTerms = [];
            const unknownClasses = [];

            obj.forEach(row => {
              if (row.errors) {
                if (row.errors.unknown_payment_terms) {
                  unknownTerms.push(...row.errors.unknown_payment_terms);
                }
                if (row.errors.unknown_customer_classes) {
                  unknownClasses.push(...row.errors.unknown_customer_classes);
                }
              }
            });

            if (props.onValidationError) {
              if (unknownTerms.length > 0) {
                props.onValidationError({ unknown_payment_terms: [...new Set(unknownTerms)] });
              }
              if (unknownClasses.length > 0) {
                props.onValidationError({ unknown_customer_classes: [...new Set(unknownClasses)] });
              }
            }

            // Remove these specific errors so they don't block import
            obj = obj.map(row => {
              if (row.errors) {
                const { unknown_payment_terms, unknown_customer_classes, ...otherErrors } = row.errors;
                return { ...row, errors: otherErrors };
              }
              return row;
            });
          }
        } else if (props.itemType === 'expense') {
          const hasUnknownExpenseCategories = obj.some(row =>
            row.errors && row.errors.unknown_expense_categories
          );

          if (hasUnknownExpenseCategories) {
            const unknownCategories = obj
              .filter(row => row.errors && row.errors.unknown_expense_categories)
              .flatMap(row => row.errors.unknown_expense_categories);

            if (props.onValidationError) {
              props.onValidationError({ unknown_expense_categories: [...new Set(unknownCategories)] });
            }

            // Remove these specific errors so they don't block import
            obj = obj.map(row => {
              if (row.errors && row.errors.unknown_expense_categories) {
                const { unknown_expense_categories, ...otherErrors } = row.errors;
                return { ...row, errors: otherErrors };
              }
              return row;
            });
          }
        }

        setValid(obj);
        setIsLoading(false);
      } catch (error) {
        console.error(error);
        setPreview(false);
        setFinished(false);
        setValid(false);
        setImportError("File formatted improperly - check for extra commas!");
        setIsLoading(false);
      }
    }
    reader.readAsText(file);
  };

  const onDrop = useCallback(acceptedFiles => {
    // Do something with the files
    acceptedFiles.forEach(file => {
      let name = file.name.toLowerCase();
      if (
        name.endsWith(".csv")
      ) {
        setResults([])
        setPreview(false)
        setFinished(false);
        setImportError(false)
        setDropped(file.name);
        fileDrop(file, staged);
      } else {
        toaster.warning("You can only upload .csv files");
      }
    });
  }, [staged]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

  const downloadFile = () => {
    const csvString = arrayToCSV(props.template)
    const fileToDownload = new Blob([csvString], { type: 'text/plain' });
    const element = document.createElement("a");
    element.href = URL.createObjectURL(fileToDownload);
    element.download = props.itemType + "-template.csv";
    document.body.appendChild(element); // Required for this to work in FireFox
    element.click();
  }

  useEffect(() => {

  }, [dropped])

  // Calculate total errors including client-side validations
  const calculateTotals = () => {
    if (!valid || !preview || !results) return { errors: false, warnings: 0 };

    try {
      let totalErrors = 0;
      let totalWarnings = 0;

      if (typeof results !== 'string') {
        console.warn('Results is not a string:', results);
        return { errors: false, warnings: 0 };
      }

      const previewData = csvToArray(results);
      if (!previewData || !Array.isArray(previewData) || previewData.length < 2) {
        console.warn('Invalid preview data:', previewData);
        return { errors: false, warnings: 0 };
      }

      previewData.slice(1).forEach((row, index) => {
        if (row !== props.template[1]) {
          // Get server-side validation results
          const serverValidation = valid[index] || { errors: {}, warnings: {} };
          // Get client-side validation results
          const clientValidation = validateRow(row, previewData[0]);

          // Add debugging logs
          console.log('Row', index + 2, 'validation:', {
            serverErrors: serverValidation.errors,
            serverWarnings: serverValidation.warnings,
            clientErrors: clientValidation.errors,
            clientWarnings: clientValidation.warnings
          });

          // Combine all errors and warnings
          const allErrors = { ...serverValidation.errors, ...clientValidation.errors };
          const allWarnings = { ...serverValidation.warnings, ...clientValidation.warnings };

          totalErrors += Object.keys(allErrors).length;
          totalWarnings += Object.keys(allWarnings).length;
        }
      });

      return { errors: totalErrors, warnings: totalWarnings };
    } catch (error) {
      console.error('Error calculating totals:', error);
      return { errors: false, warnings: 0 };
    }
  };

  const { errors: errorCount, warnings: warningCount } = calculateTotals();

  const handleValidate = async () => {
    try {
      const response = await fetch('/validate_csv/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': getCookie('csrftoken')
        },
        body: JSON.stringify({
          type: props.itemType,
          contents: results,
          pubId: currentpublisher.id
        })
      });
      const data = await response.json();
      let obj = JSON5.parse(data.message);

      // Handle customer-specific validations
      if (props.itemType === 'customer') {
        const hasUnknownPaymentTerms = obj.some(row =>
          row.errors && row.errors.unknown_payment_terms
        );
        const hasUnknownCustomerClasses = obj.some(row =>
          row.errors && row.errors.unknown_customer_classes
        );

        if (hasUnknownPaymentTerms || hasUnknownCustomerClasses) {
          const unknownTerms = [];
          const unknownClasses = [];

          obj.forEach(row => {
            if (row.errors) {
              if (row.errors.unknown_payment_terms) {
                unknownTerms.push(...row.errors.unknown_payment_terms);
              }
              if (row.errors.unknown_customer_classes) {
                unknownClasses.push(...row.errors.unknown_customer_classes);
              }
            }
          });

          if (props.onValidationError) {
            if (unknownTerms.length > 0) {
              props.onValidationError({ unknown_payment_terms: [...new Set(unknownTerms)] });
            }
            if (unknownClasses.length > 0) {
              props.onValidationError({ unknown_customer_classes: [...new Set(unknownClasses)] });
            }
          }

          // Remove these specific errors so they don't block import
          obj = obj.map(row => {
            if (row.errors) {
              const { unknown_payment_terms, unknown_customer_classes, ...otherErrors } = row.errors;
              return { ...row, errors: otherErrors };
            }
            return row;
          });
        }
      }

      setValid(obj);
      setFinished(true);
      return true;
    } catch (error) {
      console.error('Error validating CSV:', error);
      setPreview(false);
      setFinished(false);
      setValid(false);
      setImportError("Error validating CSV file");
      return false;
    }
  };

  return (
    <Pane >
      <Card paddingBottom={16} backgroundColor="white" height="100%" elevation={2}>
        <Pane display="flex" justifyContent="space-between" alignItems="center" borderBottom="solid" borderBottomColor="var(--lightgrey)" borderBottomWidth={1} paddingY={8}>
          <Paragraph size={400} fontWeight="bold" color="#5e5e5e" marginLeft={16}>Import {props.title}</Paragraph>

          <Pane>
            <Button
              marginRight={16}
              className="confirm-button"
              size="small"
              disabled={errorCount !== 0 || isLoading}
              onClick={e => { importItems(); }}
            >
              {isLoading ? (
                <>
                  <Spinner size={16} marginRight={8} />
                  Importing...
                </>
              ) : "IMPORT"}
            </Button>
            <Button size="small" marginRight={16} onClick={e => {
              props.handleToggleCsvMode();
            }}>
              Cancel
            </Button>
          </Pane>
        </Pane>

        <Pane display="flex" marginTop={16} marginX={16} justifyContent="center" >
          <Pane flex={1} display="flex" flexDirection="column">
            <Pane display="flex" alignItems="center" marginBottom={4}>
              <Paragraph size={300} fontWeight="bold" color="#5e5e5e" >FILE UPLOADER</Paragraph>
              <Tooltip content={<Pane >
                <Paragraph size={300} color="white">
                  To import multiple {props.itemType}s into WorkingLit, upload a
                  an appropriately formatted .csv file - download the template below for details.
                </Paragraph>
                <Paragraph color="red" size={300} marginTop={8}>
                  Note: Additional commas in your csv content could cause the import to
                  be corrupted.
                </Paragraph></Pane>}>
                <InfoSignIcon marginLeft={6} size={12} color="var(--lessdarkgrey)" />
              </Tooltip>
              {dropped && !finished && !importError && (
                <Pane display="flex" marginLeft={8} backgroundColor="var(--lessdarkgrey)" padding={8} borderRadius={4}>
                  <Paragraph color="white">Processing: {dropped}</Paragraph>
                  <Spinner marginLeft={8} marginTop={4} size={16} color="white" />
                </Pane>
              )}
              {dropped && !finished && importError && (
                <Pane display="flex" marginLeft={8} backgroundColor="#FEE4E2" padding={8} borderRadius={4}>
                  <ErrorIcon size={16} color="#D92D20" marginRight={8} />
                  <Paragraph color="#D92D20">Error while importing {dropped}: {importError}</Paragraph>
                </Pane>
              )}
            </Pane>
            {props.itemType === "order" && !hasCustomer ?
              <Pane display="flex" backgroundColor="var(--lightgrey)" justifyContent="center" alignItems="center" flex={1} maxHeight={174}>

                <ErrorIcon size={20} color={"var(--lessdarkgrey)"} />
                <Pane display="flex" marginLeft={8}>
                  <Paragraph color="var(--lessdarkgrey)" >Choose customer before uploading CSV</Paragraph>
                </Pane>
              </Pane>
              :
              <Pane {...getRootProps()} className="file-uploader" display="flex" justifyContent="center" alignItems="center" flex={1} maxHeight={174}>
                <input {...getInputProps()} disabled={isLoading} />
                <PaperclipIcon size={20} color={"var(--lessdarkgrey)"} />
                <Pane display="flex" marginLeft={8}>
                  {isDragActive ? <Paragraph color="var(--lessdarkgrey)" >Drop file here</Paragraph> :
                    <Pane display="flex">
                      <Paragraph color="var(--maroon)" textDecoration="underline" cursor="pointer">Choose {finished && "new"} file</Paragraph>
                      <Paragraph color="var(--lessdarkgrey)" marginLeft={4}> or drag and drop file here</Paragraph>
                    </Pane>
                  }

                </Pane>
              </Pane>
            }


          </Pane>
          {props.itemType === "order" &&
            <Pane id="customer-info" marginX={16} paddingLeft={16} width="50%">

              <Paragraph size={300} fontWeight="bold" color="#5e5e5e" >CUSTOMER INFO</Paragraph>
              <Pane display="flex" marginTop={8} >
                <Pane >
                  <Pane>
                    <Paragraph color="#667b7f" size={300}>Customer </Paragraph>
                    <AddCustomer mode={hasCustomer ? "Change" : "Choose"} handleChooseInfo={handleChooseInfo} />
                  </Pane>

                  <Pane marginTop={16}>
                    <Paragraph color="#667b7f" size={300}>Email Address </Paragraph>
                    {hasCustomer ? (
                      <AddContactInfo
                        mode={hasEmail ? "Change" : "Choose"}
                        handleChooseInfo={handleChooseInfo}
                        itemSet={staged.customer.customeremailSet}
                        itemType="email"
                      />
                    ) : (
                      <Paragraph size={300} marginTop={4} color="grey" marginBottom={16}>(CHOOSE CUSTOMER FIRST)</Paragraph>
                    )}

                  </Pane>

                </Pane>

                <Pane marginX={32}>
                  <Pane display="flex">
                    <Pane>
                      <Paragraph color="#667b7f" size={300}>Phone Number </Paragraph>
                      {hasCustomer ? (
                        <AddContactInfo
                          mode={hasPhone ? "Change" : "Choose"}
                          handleChooseInfo={handleChooseInfo}
                          itemSet={staged.customer.customerphonenumberSet}
                          itemType="phone"
                        />
                      ) : (
                        <Paragraph size={300} marginTop={4} color="grey" marginBottom={16}>(CHOOSE CUSTOMER FIRST)</Paragraph>
                      )}
                    </Pane>

                  </Pane>

                  <Pane marginTop={24}>
                    <Paragraph color="#667b7f" size={300}>Shipping Address </Paragraph>
                    {hasCustomer ? (
                      <AddContactInfo
                        mode={hasAddress ? "Change" : "Choose"}
                        handleChooseInfo={handleChooseInfo}
                        itemSet={staged.customer.customeraddressSet}
                        itemType="address"
                      />
                    ) : (
                      <Paragraph size={300} marginTop={4} color="grey" marginBottom={16}>(CHOOSE CUSTOMER FIRST)</Paragraph>
                    )}
                  </Pane>
                </Pane>
                {/* Help Box positioned to the right of phone number */}
                <Pane marginLeft={32}>
                  <HelpBox />
                </Pane>
              </Pane>


            </Pane>}
          {/* {dropped && finished && (
            <Pane marginLeft={16} flex="2" overflowX="scroll">
              <Pane display="flex" alignItems="center" marginBottom={8} >
                <Paragraph size={300} fontWeight="bold" color="#5e5e5e">IMPORT STATUS</Paragraph>
                <Badge marginLeft={8} color={errorCount ? "red" : "green"}>{errorCount} Errors</Badge>
                <Badge marginLeft={8} color={warningCount ? "yellow" : "green"}>{warningCount} Warnings</Badge>
              </Pane>
              <Table.Head className="error-table" height={16}>
                <Table.TextCell flex={1} textAlign="center"><Paragraph size={300} fontWeight="bold" >ROW #</Paragraph></Table.TextCell>
                <Table.TextCell flex={3}>ERRORS</Table.TextCell>
                <Table.TextCell flex={3}>WARNINGS</Table.TextCell>

              </Table.Head>
              <Pane maxHeight={150} overflowY="scroll" borderBottom="default" borderLeft="default" borderRight="default">
                {Object.keys(valid).map(row => {
                  if (Object.keys(valid[row].errors).length || Object.keys(valid[row].warnings).length)
                    return (
                      <Table.Row className="table-row">
                        <Table.TextCell textAlign="center"><Paragraph size={300} fontWeight="bold">{row}</Paragraph></Table.TextCell>
                        <Table.TextCell flex={3}>
                          {Object.keys(valid[row].errors).map(col => { return <Paragraph size={300} marginBottom={4}>{col}: {valid[row].errors[col]}</Paragraph> })}
                        </Table.TextCell>
                        <Table.TextCell flex={3}>
                          {Object.keys(valid[row].warnings).map(col => { return <Paragraph size={300} marginBottom={4}>{col}: {valid[row].warnings[col]}</Paragraph> })}
                        </Table.TextCell>
                      </Table.Row>)
                }
                )}

              </Pane>

            </Pane>
          )} */}
        </Pane>

        <Pane marginTop={16} marginX={16} backgroundColor="white" >
          {!finished && !preview && (
            <Pane>
              <Pane display="flex" alignItems="center">
                <Paragraph size={300} fontWeight="bold" color="#5e5e5e">EXAMPLE FILE</Paragraph>
                <Button marginLeft={16} size="small" onClick={e => {
                  downloadFile();
                }}>
                  Download Template
                </Button></Pane>
              <Pane marginTop={8} className="csvTable">
                <CsvExample csvTemplate={props.template} tableWidth={props.tableWidth} colWidths={props.colWidths} />
              </Pane>
            </Pane>

          )}
          {preview && (
            <Pane>
              <Pane display="flex" flexDirection="column">
                <Pane display="flex" alignItems="center">
                  <Paragraph size={300} fontWeight="bold" color="#5e5e5e">IMPORT PREVIEW</Paragraph>
                  {results.length && <Badge marginLeft={8} color={"neutral"}>{csvToArray(results).length} Rows</Badge>}
                  {!finished ? (
                    <Pane
                      marginLeft={8}
                      backgroundColor="var(--lessdarkgrey)"
                      padding={8}
                      borderRadius={4}
                      display="flex"
                      alignItems="center"
                    >
                      <Spinner size={16} marginRight={8} color="white" />
                      <Paragraph color="white">Checking for errors...</Paragraph>
                    </Pane>
                  ) : (
                    <>
                      <Badge
                        marginLeft={8}
                        color={errorCount > 0 ? "red" : "green"}
                      >
                        {errorCount || 0} Error{errorCount !== 1 ? 's' : ''}
                      </Badge>
                      <Badge
                        marginLeft={8}
                        color={warningCount > 0 ? "yellow" : "green"}
                      >
                        {warningCount || 0} Warning{warningCount !== 1 ? 's' : ''}
                      </Badge>
                      {errorCount > 0 && (
                        <Pane marginLeft={8}>
                          <Paragraph size={300} color="red">
                            Errors found. Check the validation results below:
                          </Paragraph>
                          {valid && Object.keys(valid).map((rowIndex) => {
                            const rowErrors = valid[rowIndex].errors;
                            if (Object.keys(rowErrors).length > 0) {
                              return (
                                <Pane key={rowIndex} marginY={4}>
                                  <Paragraph size={300}>
                                    Row {parseInt(rowIndex) + 2}: {
                                      Object.entries(rowErrors).map(([field, error]) =>
                                        `${field}: ${error}`
                                      ).join(', ')
                                    }
                                  </Paragraph>
                                </Pane>
                              );
                            }
                            return null;
                          })}
                        </Pane>
                      )}
                    </>
                  )}
                </Pane>
                <Pane marginTop={8} className="csvTable">
                  <CsvPreview csvPreview={results} template={props.template} validation={valid} tableWidth={props.tableWidth} colWidths={props.colWidths} />
                </Pane>
              </Pane>
            </Pane>
          )}
        </Pane>
      </Card>
    </Pane>
  )
}

const CsvExample = (props) => {

  return (
    <Pane width={props.tableWidth} elevation={1}>
      <Pane overflow="auto" borderBottom="default">
        <Table.Head className="table-heading">
          {props.csvTemplate[0].map((item, index) => {

            return (
              <Table.TextCell key={item} flex={props.colWidths[index]}
              ><Text className="breakable" size={300} >{item}</Text></Table.TextCell>
            )
          })}
        </Table.Head>
        <Table.Row className="table-row csvDescription">
          {props.csvTemplate[1].map((item, index) => {

            return (
              <Table.TextCell key={`desc${index}`} flex={props.colWidths[index]}><Text className="breakable" size={300} >{item}</Text></Table.TextCell>

            )
          })}
        </Table.Row>

        {props.csvTemplate.slice(2).map((row, rowIndex) =>
          <Table.Row className="table-row">
            {row.map((item, index) =>
              <Table.TextCell key={`${rowIndex}ex${index}`} flex={props.colWidths[index]}><Text className="breakable" size={300}>{item}</Text></Table.TextCell>
            )}
          </Table.Row>
        )}
      </Pane>

    </Pane>

  )
}

const CsvPreview = (props) => {
  if (props.csvPreview.length > 0) {
    const previewData = csvToArray(props.csvPreview)

    return (
      <Pane width={props.tableWidth}>
        <Pane>
          <Table.Head key="previewHeader" className="table-heading">
            <Table.TextCell textAlign="center" padding={4} flex={2}>
              CSV Row
            </Table.TextCell>
            {previewData[0].map((item, index) => {
              return (
                <Table.TextCell key={`preview${item}`} flex={props.colWidths[index]} >{item}</Table.TextCell>
              )
            })}
          </Table.Head>
          <Pane maxHeight={500} overflowY="scroll">
            {previewData.slice(1).map((row, rowIndex) => {
              if (row !== props.template[1]) {
                // Combine server and client validations
                const serverValidation = props.validation[rowIndex] || { errors: {}, warnings: {} };
                const clientValidation = validateRow(row, previewData[0]);
                let errors = { ...serverValidation.errors, ...clientValidation.errors };
                let warnings = { ...serverValidation.warnings, ...clientValidation.warnings };
                let rowColor = props.validation[rowIndex] ? props.validation[rowIndex].duplicate.length ? "#FFEFD2" : "white" : "white"
                let reference = props.validation[rowIndex] ? props.validation[rowIndex].reference ? true : false : false
                return (
                  <Table.Row
                    key={`row-${rowIndex}`}
                    className="table-row"
                    borderBottom={reference ? "dashed" : "none"}
                    border={reference ? "dashed" : "none"}
                    borderColor="var(--lessdarkgrey)"
                  >
                    <Table.TextCell textAlign="center" padding={4} backgroundColor={rowColor} flex={2}>
                      {(rowColor !== "white" || reference) && <Tooltip content={
                        <Pane>
                          {reference &&
                            <Paragraph size={300} color="white">
                              Reference row for non-product fields
                            </Paragraph>}
                          {props.validation[rowIndex].duplicate.includes("app") &&
                            <Paragraph size={300} color="white">
                              Potential duplicate found in WorkingLit
                            </Paragraph>}
                          {props.validation[rowIndex].duplicate.includes("csv") &&
                            <Paragraph size={300} color="white">
                              Potential duplicate found in this CSV
                            </Paragraph>}
                        </Pane>
                      }>
                        <InfoSignIcon color="muted" size={12} marginLeft={4} />
                      </Tooltip>}
                      <Paragraph size={300}> {rowIndex + 2}
                      </Paragraph>

                    </Table.TextCell>
                    {row.map((item, colIndex) => {
                      let colName = previewData[0][colIndex]
                      let cellColor = colName in errors ? "#F9DADA" : colName in warnings ? "#FFEFD2" : "white"
                      return (
                        <Table.TextCell
                          key={`${rowIndex}-${colIndex}`}
                          backgroundColor={cellColor}
                          flex={props.colWidths[colIndex]}
                          textAlign="center"
                          padding={4}
                        >
                          {(colName in errors || colName in warnings) &&
                            <Tooltip content={
                              <Paragraph size={300} color="white">
                                {errors[colName]} {warnings[colName]}
                              </Paragraph>
                            }>
                              <InfoSignIcon color="muted" size={12} marginLeft={4} />
                            </Tooltip>
                          }
                          <Pane>
                            <Text className="breakable" size={300} >{item}</Text>
                          </Pane>

                        </Table.TextCell>
                      )
                    })}
                  </Table.Row>
                )
              } else {
                return <Pane></Pane>
              }
            })}
          </Pane>
        </Pane>
      </Pane>
    )
  }
  else {
    return (
      <Pane>{props.csvPreview}</Pane>
    )
  }
}

export default CsvUpload;
